import React from "react";
import { Box, Typography } from "@mui/material";
import SentimentVerySatisfiedIcon from "@mui/icons-material/SentimentVerySatisfied";
import SentimentNeutralIcon from "@mui/icons-material/SentimentNeutral";
import SentimentVeryDissatisfiedIcon from "@mui/icons-material/SentimentVeryDissatisfied";
import PlaceIcon from "@mui/icons-material/Place";
import { getNpsBreakdown } from "../../utils/nps";

// Distance (px) between the card's left border and the chart
const GAUGE_LEFT_OFFSET = -10;

const GAUGE_W = 68;
const GAUGE_H = 72;
const CX = 34;
const CY = 33;
const R = 27;
const TRACK_STROKE = 4;
const FILL_STROKE = 6;

const isLikelyCeiScore = (score) => {
  const n = Number(score);
  return !Number.isNaN(n) && n > 0 && n <= 5;
};

const polar = (cx, cy, r, deg) => {
  const rad = (deg * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy - r * Math.sin(rad) };
};

// Draws clockwise (in screen space) from startDeg to endDeg
const arcPath = (cx, cy, r, startDeg, endDeg) => {
  const s = polar(cx, cy, r, startDeg);
  const e = polar(cx, cy, r, endDeg);
  const largeArc = startDeg - endDeg > 180 ? 1 : 0;
  return `M ${s.x} ${s.y} A ${r} ${r} 0 ${largeArc} 1 ${e.x} ${e.y}`;
};

// 0 = top center (90deg). Positive sweeps clockwise down the right side to
// bottom (+100 = -90deg), negative counterclockwise down the left (-100 = 270deg).
const npsToAngle = (nps) =>
  90 - (Math.max(-100, Math.min(100, nps)) / 100) * 180;

const NpsGauge = ({ value, delta }) => {
  const numericValue = Number(value) || 0;
  const clamped = Math.max(-100, Math.min(100, numericValue));
  const scoreAngle = npsToAngle(clamped);
  const pointer = polar(CX, CY, R, scoreAngle);
  const isPositive = clamped >= 0;

  const displayValue =
    value === "" || value == null ? "-" : numericValue.toFixed(2);

  const deltaNum = Number(delta);
  const previousScore = numericValue - deltaNum;
  const hasValidDelta =
    delta !== "" &&
    delta != null &&
    !Number.isNaN(deltaNum) &&
    !isLikelyCeiScore(previousScore);

  const deltaText = hasValidDelta
    ? deltaNum > 0
      ? `+${deltaNum.toFixed(2)}`
      : deltaNum === 0
      ? "="
      : deltaNum.toFixed(2)
    : null;

  return (
    <Box
      sx={{
        position: "relative",
        width: GAUGE_W,
        height: GAUGE_H,
        flexShrink: 0,
        ml: `${GAUGE_LEFT_OFFSET}px`,
      }}
    >
      <svg
        width={GAUGE_W}
        height={GAUGE_H}
        viewBox={`0 0 ${GAUGE_W} ${GAUGE_H}`}
        style={{ display: "block", overflow: "visible" }}
      >
        {/* light red track: left half, top center down to bottom */}
        <path
          d={arcPath(CX, CY, R, 270, 90)}
          fill="none"
          stroke="#f6c9c9"
          strokeWidth={TRACK_STROKE}
          strokeLinecap="round"
        />
        {/* light green track: right half, top center down to bottom */}
        <path
          d={arcPath(CX, CY, R, 90, -90)}
          fill="none"
          stroke="#c4e6c6"
          strokeWidth={TRACK_STROKE}
          strokeLinecap="round"
        />
        {/* bold fill from top center toward score: right if positive, left if negative */}
        {clamped !== 0 &&
          (isPositive ? (
            <path
              d={arcPath(CX, CY, R, 90, scoreAngle)}
              fill="none"
              stroke="#2eae44"
              strokeWidth={FILL_STROKE}
              strokeLinecap="round"
            />
          ) : (
            <path
              d={arcPath(CX, CY, R, scoreAngle, 90)}
              fill="none"
              stroke="#d93636"
              strokeWidth={FILL_STROKE}
              strokeLinecap="round"
            />
          ))}
        {/* white knob at score position */}
        <circle
          cx={pointer.x}
          cy={pointer.y}
          r={4}
          fill="#fff"
          stroke="#777"
          strokeWidth={1.5}
        />
        <text
          x={CX - R + 8}
          y={CY + R + 8}
          textAnchor="middle"
          fontSize={7.5}
          fill="#999"
        >
          -100
        </text>
        <text
          x={CX + R - 8}
          y={CY + R + 8}
          textAnchor="middle"
          fontSize={7.5}
          fill="#999"
        >
          100
        </text>
      </svg>

      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: CY * 2,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          pointerEvents: "none",
        }}
      >
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 700,
            lineHeight: 1.1,
            color: "#1e2a3a",
            letterSpacing: -0.3,
          }}
        >
          {displayValue}
        </Typography>
        {deltaText != null && (
          <Typography
            sx={{
              fontSize: 8.5,
              fontWeight: 500,
              lineHeight: 1.3,
              color: "#1e2a3a",
            }}
          >
            {deltaText}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const SmileyStat = ({ icon: Icon, color, percent, count }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.3, py: 0.1 }}>
    <Icon sx={{ fontSize: 14, color }} />
    <Typography sx={{ fontSize: 9, color: "#555", whiteSpace: "nowrap" }}>
      {percent}% ({count})
    </Typography>
  </Box>
);

export const NpsGaugeCard = ({
  title,
  value,
  delta,
  promoters,
  passives,
  detractors,
  periodLabel,
}) => {
  const breakdown = getNpsBreakdown(promoters, passives, detractors);
  const previousScore =
    value !== "" && value != null && delta !== "" && delta != null
      ? Number(value) - Number(delta)
      : null;
  const showPrevious =
    previousScore != null &&
    !Number.isNaN(previousScore) &&
    !isLikelyCeiScore(previousScore);

  return (
    <Box
      sx={{
        bgcolor: "#fff",
        borderRadius: 1.5,
        border: "1px solid #e8e8e8",
        p: 0.75,
        minWidth: 0,
        flex: 1,
      }}
    >
      <Typography
        sx={{ fontSize: 11, fontWeight: 700, color: "#1a1a1a", lineHeight: 1.2 }}
      >
        {title}
      </Typography>
      <Typography
        sx={{ fontSize: 9, color: "#888", mb: 0.25, lineHeight: 1.2 }}
      >
       Likelihood to Recommend
      </Typography>

      <Box sx={{ display: "flex", alignItems: "center", gap: 0 }}>
        <NpsGauge value={value} delta={delta} />
        {breakdown.total > 0 && (
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <SmileyStat
              icon={SentimentVerySatisfiedIcon}
              color="#4caf50"
              percent={breakdown.promoterPercent}
              count={breakdown.promoters}
            />
            <SmileyStat
              icon={SentimentNeutralIcon}
              color="#9e9e9e"
              percent={breakdown.passivePercent}
              count={breakdown.passives}
            />
            <SmileyStat
              icon={SentimentVeryDissatisfiedIcon}
              color="#9e9e9e"
              percent={breakdown.detractorPercent}
              count={breakdown.detractors}
            />
          </Box>
        )}
      </Box>

      {showPrevious && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.3, mt: 0.35 }}>
          <PlaceIcon sx={{ fontSize: 10, color: "#888" }} />
          <Typography sx={{ fontSize: 9, color: "#666" }}>
            Last Period: {previousScore.toFixed(2)}
          </Typography>
        </Box>
      )}

      <Typography sx={{ fontSize: 8, color: "#bbb", mt: 0.25 }}>
        NPS = %4-5 − %1-2
      </Typography>
    </Box>
  );
};

const NpsGaugeWidget = ({ store, periodLabel }) => (
  <Box
    sx={{
      display: "flex",
      gap: 0.75,
      width: "100%",
      maxWidth: 340,
      mx: "auto",
    }}
  >
    <NpsGaugeCard
      title="Sales NPS"
      value={store.sales}
      delta={store.salesChange}
      promoters={store.salesPromoters}
      passives={store.salesPassives}
      detractors={store.salesDetractors}
      periodLabel={periodLabel}
    />
    <NpsGaugeCard
      title="Service NPS"
      value={store.service}
      delta={store.serviceChange}
      promoters={store.servicePromoters}
      passives={store.servicePassives}
      detractors={store.serviceDetractors}
      periodLabel={periodLabel}
    />
  </Box>
);

export default NpsGaugeWidget;
